import { Formik } from 'formik';
import React, { useContext, useState, useCallback } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Input from 'components/FormFields/Input';
import PhoneInput from 'components/FormFields/PhoneInput';
import Loading from 'components/Loading';
import PageHeaderIcon from 'components/PageHeaderIcon';
import { useMe } from 'hooks/useMe';
import Capabilities from 'pages/app/Users/UserEdit/Capabilities';
import { AppContext } from 'shared/AppContext';
import {
  useGetEmployee,
  useSearchUsers,
  useCreateEmployee,
  useUpdateEmployee,
} from './gql/useEmployeeMutations';

// Page Container
const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background || '#0F172A'
      : '#FFFFFF'};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Header components
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

// Content wrapper
const ContentWrapper = styled.div`
  padding: 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Form sections
const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
`;

const InfoText = styled.p`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.secondary || '#94A3B8'
      : '#64748B'};
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

// Form grid
const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

// Suggestions for phone search
const SuggestionsList = styled.ul`
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 8px;
  list-style: none;
  margin: 8px 0 0 0;
  padding: 0;
  max-height: 180px;
  overflow-y: auto;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1E293B' : '#FFFFFF'};
`;

const SuggestionItem = styled.li`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)'};
  }
`;

const NoResults = styled.div`
  padding: 12px 16px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.secondary || '#94A3B8'
      : '#64748B'};
  font-size: 14px;
`;

// Error text for validation messages
const ErrorText = styled.span`
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  display: block;
`;

// Validation summary
const ValidationSummary = styled.div`
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.3);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const ValidationTitle = styled.div`
  color: #dc2626;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
`;

const ValidationList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: #dc2626;
  font-size: 13px;

  li {
    margin-bottom: 4px;
  }
`;

// Selected user display
const SelectedUser = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 8px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.02)'};
  margin-bottom: 16px;
`;

const SelectedUserName = styled.span`
  flex: 1;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  font-weight: 500;
`;

const AccountChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.2)'
      : 'rgba(250, 70, 22, 0.1)'};
  border-radius: 20px;
  margin: 4px;
  font-size: 14px;
  color: ${(props) => props.theme.colors?.primary || '#FA4616'};
`;

const RemoveChipBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.colors?.primary || '#FA4616'};

  &:hover {
    opacity: 0.7;
  }
`;

const AccountSelect = styled.select`
  width: 100%;
  padding: 12px 36px 12px 16px;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 8px;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1E293B' : '#FFFFFF'};
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  font-size: 14px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors?.primary || '#FA4616'};
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin-bottom: 8px;
  display: block;
`;

// Tabs for accounts
const TabsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
`;

const Tab = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props) =>
    props.active
      ? props.theme.colors?.primary || '#FA4616'
      : props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.05)'};
  color: ${(props) =>
    props.active
      ? '#FFFFFF'
      : props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};

  &:hover {
    background: ${(props) =>
      props.active
        ? props.theme.colors?.primary || '#FA4616'
        : props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  }
`;

// Capability keys
const capabilityKeys = [
  'account_id',
  'events',
  'guests',
  'add_guests',
  'messages',
  'add_promos',
  'promos',
  'credits',
  'issue_credits',
  'transactions',
  'issue_refunds',
  'registrations',
  'customers',
  'memberships',
  'staff',
  'add_staff',
  'stripe',
  'scan_tickets',
  'check_tickets',
  'create_messages',
  'edit_registrations',
  'check_in_guests',
  'edit_memberships',
  'edit_billing',
  'pos',
  'cancel_event',
  'registration_email',
  'annual_email',
  'membership_email',
  'refund_request_email',
];

const TrackEmployeeForm = () => {
  const history = useHistory();
  const params = useParams();
  const { pathname } = useLocation();
  const employeeId = params.id ? parseInt(params.id) : null;
  const isEditMode = !!employeeId;

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const {
    state: { user },
  } = useContext(AppContext);

  const {
    data: { me },
  } = useMe();
  const isSelfEdit = me.id === employeeId;

  // Get employee data if editing
  const { data: employeeData, loading: employeeLoading } =
    useGetEmployee(employeeId);
  const employee = employeeData?.getEmployeeV2;

  // Mutations
  const { createEmployee, loading: createLoading } = useCreateEmployee();
  const { updateEmployee, loading: updateLoading } = useUpdateEmployee();

  // Phone search
  const { search: searchUsers, loading: searchLoading } = useSearchUsers();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchPhone, setSearchPhone] = useState('');
  const [searchCountryCode, setSearchCountryCode] = useState('+1');

  // Active tab for permissions
  const [activeTab, setActiveTab] = useState(0);

  // Platform name
  const platform =
    process.env.REACT_APP_PLATFORM === 'tickethoss'
      ? 'Ticket Hoss'
      : process.env.REACT_APP_PLATFORM === 'pitpay'
      ? 'Pit Pay'
      : 'Kart Pass';

  // Get available accounts
  const accounts = employee
    ? employee?.accounts.map((account) => ({
        label: account.business_dba,
        value: account.id,
      }))
    : user.accounts.map((account) => ({
        label: account.business_dba,
        value: account.id,
      }));

  // Initial form values
  const getInitialValues = () => {
    if (isEditMode && employee) {
      return {
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        cellphone:
          employee.cellphone && !employee.cellphone.includes('+')
            ? `+${employee.calling_code || '1'}${employee.cellphone}`
            : employee.cellphone || '',
        country_code: employee.country_code || 'US',
        calling_code: employee.calling_code || '1',
        email: employee.email || '',
        account_ids: accounts,
        capabilities: employee.capabilities || [],
        user_id: employeeId,
      };
    }

    // For new employee, initialize capabilities for all accounts
    const initialCapabilities = accounts.map((account) =>
      capabilityKeys.reduce(
        (acc, key) => ({
          ...acc,
          [key]: key === 'account_id' ? account.value : false,
        }),
        {}
      )
    );

    return {
      first_name: '',
      last_name: '',
      cellphone: '',
      country_code: 'US',
      calling_code: '1',
      email: '',
      account_ids: accounts,
      capabilities: initialCapabilities,
      user_id: null,
    };
  };

  // Validate form
  const validate = (values) => {
    const errors = {};

    if (!values.cellphone) {
      errors.cellphone = 'Phone number is required';
    }

    if (!values.first_name) {
      errors.first_name = 'First name is required';
    }

    if (!values.last_name) {
      errors.last_name = 'Last name is required';
    }

    if (!values.email) {
      errors.email = 'Email is required';
    }

    return errors;
  };

  // Handle phone search
  const handlePhoneSearch = useCallback(
    async (phoneValue, setFieldValue) => {
      if (phoneValue && phoneValue.length > 10) {
        const results = await searchUsers(phoneValue);
        if (results.length > 0) {
          setSuggestions(
            results.map((item) => ({
              label: `${item.first_name} ${item.last_name}`,
              user_id: item.id,
              first_name: item.first_name,
              last_name: item.last_name,
              cellphone: item.cellphone,
              country_code: item.country_code || 'US',
              calling_code: item.calling_code || '1',
              email: item.email || '',
            }))
          );
          setShowSuggestions(true);
          setShowForm(false);
        } else {
          setSuggestions([]);
          setShowSuggestions(true);
          setShowForm(true);
          setFieldValue('cellphone', phoneValue);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    },
    [searchUsers]
  );

  // Handle selecting a user from suggestions
  const handleSelectUser = (user, setFieldValue) => {
    setFieldValue('first_name', user.first_name);
    setFieldValue('last_name', user.last_name);
    setFieldValue('email', user.email);
    setFieldValue(
      'cellphone',
      user.cellphone.includes('+')
        ? user.cellphone
        : `+${user.calling_code}${user.cellphone}`
    );
    setFieldValue('country_code', user.country_code);
    setFieldValue('calling_code', user.calling_code);
    setFieldValue('user_id', user.user_id);
    setSelectedUser(user);
    setSuggestions([]);
    setShowSuggestions(false);
    setShowForm(false);
  };

  // Handle removing selected user
  const handleRemoveUser = (setFieldValue) => {
    setSelectedUser(null);
    setFieldValue('first_name', '');
    setFieldValue('last_name', '');
    setFieldValue('email', '');
    setFieldValue('cellphone', '');
    setFieldValue('user_id', null);
    setShowForm(false);
    setShowSuggestions(false);
  };

  // Handle account selection
  const handleAddAccount = (accountId, values, setFieldValue) => {
    const account = accounts.find((a) => a.value === parseInt(accountId));
    if (account && !values.account_ids.find((a) => a.value === account.value)) {
      const newAccountIds = [...values.account_ids, account];
      setFieldValue('account_ids', newAccountIds);

      // Add capabilities for new account
      const newCapability = capabilityKeys.reduce(
        (acc, key) => ({
          ...acc,
          [key]: key === 'account_id' ? account.value : false,
        }),
        {}
      );
      setFieldValue('capabilities', [...values.capabilities, newCapability]);
    }
  };

  const handleRemoveAccount = (accountValue, values, setFieldValue) => {
    const newAccountIds = values.account_ids.filter(
      (a) => a.value !== accountValue
    );
    setFieldValue('account_ids', newAccountIds);

    // Remove capabilities for this account
    const newCapabilities = values.capabilities.filter(
      (c) => c.account_id !== accountValue
    );
    setFieldValue('capabilities', newCapabilities);

    // Adjust active tab if needed
    if (activeTab >= newAccountIds.length && newAccountIds.length > 0) {
      setActiveTab(newAccountIds.length - 1);
    }
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);

    const cleanedCapabilities = values.capabilities.map((capability) => {
      const { ...rest } = capability;
      return rest;
    });

    const inputData = {
      first_name: values.first_name,
      last_name: values.last_name,
      cellphone: values.calling_code
        ? values.cellphone.replace(`+${values.calling_code}`, '')
        : values.cellphone.replace('+1', ''),
      country_code: values.country_code,
      calling_code: values.calling_code,
      email: values.email,
      account_ids: values.account_ids.map((a) => a.value),
      capabilities: cleanedCapabilities,
    };

    if (isEditMode) {
      inputData.user_id = employeeId;
    }

    const response = isEditMode
      ? await updateEmployee(inputData)
      : await createEmployee(inputData);

    setSubmitting(false);

    if (!response || response.errors) {
      toast.error(
        isEditMode
          ? 'Error updating staff member'
          : 'Error creating staff member'
      );
      return;
    }

    toast.success(isEditMode ? 'Staff member updated' : 'Staff member created');
    resetForm();
    history.push(`${routePrefix}/employees`);
  };

  // Handle capability change
  const handleCapabilityChange = (
    capIndex,
    field,
    value,
    values,
    setFieldValue
  ) => {
    const newCapabilities = values.capabilities.map((cap, index) =>
      index === capIndex ? { ...cap, [field]: value } : cap
    );
    setFieldValue('capabilities', newCapabilities);
  };

  // Handle multiple capability changes (for "All Access")
  const handleMultipleCapabilityChange = (
    capIndex,
    changes,
    values,
    setFieldValue
  ) => {
    const newCapabilities = values.capabilities.map((cap, index) =>
      index === capIndex ? { ...cap, ...changes } : cap
    );
    setFieldValue('capabilities', newCapabilities);
  };

  if (employeeLoading) {
    return (
      <EnhancedThemeProvider>
        <PageContainer>
          <Loading />
        </PageContainer>
      </EnhancedThemeProvider>
    );
  }

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        <Formik
          enableReinitialize
          initialValues={getInitialValues()}
          validate={validate}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setValues,
            isSubmitting,
            submitCount,
          }) => {
            // Show errors after form submission attempt
            const showError = (field) =>
              (touched[field] || submitCount > 0) && errors[field];
            const hasErrors = submitCount > 0 && Object.keys(errors).length > 0;

            return (
              <form onSubmit={handleSubmit}>
                {/* Header Card */}
                <Card style={{ marginBottom: '30px' }}>
                  <HeaderContainer>
                    <HeaderLeft>
                      <PageHeaderIcon icon="users" />
                      <HeaderTitle>
                        {isEditMode
                          ? 'Edit Staff Member'
                          : 'Create New Staff Member'}
                      </HeaderTitle>
                    </HeaderLeft>
                    <ButtonV2
                      type="submit"
                      disabled={isSubmitting || createLoading || updateLoading}
                      style={{ width: 'auto' }}
                    >
                      {isSubmitting || createLoading || updateLoading
                        ? 'Saving...'
                        : isEditMode
                        ? 'Update Staff Member'
                        : 'Add Staff Member'}
                    </ButtonV2>
                  </HeaderContainer>
                </Card>

                {/* Main Form Card */}
                <Card noPadding>
                  <ContentWrapper>
                    {/* Validation Summary */}
                    {hasErrors && (
                      <ValidationSummary>
                        <ValidationTitle>
                          Please fix the following errors:
                        </ValidationTitle>
                        <ValidationList>
                          {errors.first_name && <li>{errors.first_name}</li>}
                          {errors.last_name && <li>{errors.last_name}</li>}
                          {errors.email && <li>{errors.email}</li>}
                          {errors.cellphone && <li>{errors.cellphone}</li>}
                          {errors.account_ids && <li>{errors.account_ids}</li>}
                        </ValidationList>
                      </ValidationSummary>
                    )}

                    {/* Phone Search Section (only for new employee) */}
                    {!isEditMode && (
                      <Section>
                        <SectionTitle>Find Staff Member</SectionTitle>
                        <InfoText>
                          Enter the mobile number of the staff member you wish
                          to add.
                        </InfoText>
                        <InfoText>
                          If they are already a {platform} App user, their
                          number will autopopulate and their {platform} profile
                          will be updated once you select their name and tap to
                          add staff member.
                        </InfoText>
                        <InfoText>
                          If they are not a {platform} App user, you will see a
                          form to add them. Fill out the additional fields as
                          required and tap to add staff member.
                        </InfoText>

                        {selectedUser ? (
                          <SelectedUser>
                            <SelectedUserName>
                              {selectedUser.label}
                            </SelectedUserName>
                            <ButtonV2
                              variant="secondary"
                              type="button"
                              onClick={() => handleRemoveUser(setFieldValue)}
                              style={{ padding: '8px 16px', fontSize: '14px' }}
                            >
                              Clear
                            </ButtonV2>
                          </SelectedUser>
                        ) : (
                          <FormGroup>
                            <PhoneInput
                              id="search_phone"
                              label="Search Phone Number"
                              value={searchPhone}
                              onChange={(value) => {
                                setSearchPhone(value);
                                // Build full phone number for search
                                const fullPhone = `${searchCountryCode}${value.replace(
                                  /\D/g,
                                  ''
                                )}`;
                                handlePhoneSearch(fullPhone, setFieldValue);
                              }}
                              countryCode={searchCountryCode}
                              onCountryChange={(code) => {
                                setSearchCountryCode(code);
                                // Extract calling code number from code (e.g., '+1' -> '1')
                                const callingCode = code.replace('+', '');
                                setFieldValue('calling_code', callingCode);
                              }}
                            />
                            {searchLoading && <Loading size={30} />}
                            {showSuggestions && suggestions.length > 0 && (
                              <SuggestionsList>
                                {suggestions.map((suggestion, index) => (
                                  <SuggestionItem
                                    key={index}
                                    onClick={() =>
                                      handleSelectUser(
                                        suggestion,
                                        setFieldValue,
                                        setValues,
                                        values
                                      )
                                    }
                                  >
                                    {suggestion.label}
                                  </SuggestionItem>
                                ))}
                              </SuggestionsList>
                            )}
                            {showSuggestions &&
                              suggestions.length === 0 &&
                              !searchLoading && (
                                <NoResults>
                                  No match found. Fill out the form below to add
                                  a new staff member.
                                </NoResults>
                              )}
                          </FormGroup>
                        )}
                      </Section>
                    )}

                    {/* Account Selection */}
                    <Section>
                      <SectionTitle>Track or Series</SectionTitle>
                      <FormGroup>
                        <Label>Select Accounts</Label>
                        <div style={{ marginBottom: '16px' }}>
                          {values.account_ids.map((account) => (
                            <AccountChip key={account.value}>
                              {account.label}
                              <RemoveChipBtn
                                type="button"
                                onClick={() =>
                                  handleRemoveAccount(
                                    account.value,
                                    values,
                                    setFieldValue
                                  )
                                }
                              >
                                ×
                              </RemoveChipBtn>
                            </AccountChip>
                          ))}
                        </div>
                        <AccountSelect
                          onChange={(e) =>
                            handleAddAccount(
                              e.target.value,
                              values,
                              setFieldValue
                            )
                          }
                          value=""
                        >
                          <option value="">Add account...</option>
                          {accounts
                            .filter(
                              (a) =>
                                !values.account_ids.find(
                                  (selected) => selected.value === a.value
                                )
                            )
                            .map((account) => (
                              <option key={account.value} value={account.value}>
                                {account.label}
                              </option>
                            ))}
                        </AccountSelect>
                        {showError('account_ids') && (
                          <ErrorText>{errors.account_ids}</ErrorText>
                        )}
                      </FormGroup>
                    </Section>

                    {/* Staff Details Form (show when editing or when new user form should appear) */}
                    {(isEditMode || showForm || selectedUser) && (
                      <Section>
                        <SectionTitle>Staff Details</SectionTitle>
                        <FormRow>
                          <FormGroup>
                            <PhoneInput
                              id="cellphone"
                              label="Phone Number"
                              value={
                                values.cellphone
                                  ? values.cellphone.replace(
                                      `+${values.calling_code || '1'}`,
                                      ''
                                    )
                                  : ''
                              }
                              onChange={(value) => {
                                // Store with calling code prefix
                                const fullPhone = `+${
                                  values.calling_code || '1'
                                }${value.replace(/\D/g, '')}`;
                                setFieldValue('cellphone', fullPhone);
                              }}
                              countryCode={`+${values.calling_code || '1'}`}
                              onCountryChange={(code) => {
                                const callingCode = code.replace('+', '');
                                setFieldValue('calling_code', callingCode);
                                // Update cellphone with new calling code
                                if (values.cellphone) {
                                  const phoneOnly = values.cellphone.replace(
                                    /^\+\d+/,
                                    ''
                                  );
                                  setFieldValue(
                                    'cellphone',
                                    `+${callingCode}${phoneOnly}`
                                  );
                                }
                              }}
                            />
                            {showError('cellphone') && (
                              <ErrorText>{errors.cellphone}</ErrorText>
                            )}
                          </FormGroup>
                          <FormGroup>
                            <Input
                              name="first_name"
                              label="First Name"
                              placeholder="First Name"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.first_name}
                              error={!!showError('first_name')}
                              helperText={showError('first_name')}
                            />
                          </FormGroup>
                        </FormRow>
                        <FormRow style={{ marginTop: '16px' }}>
                          <FormGroup>
                            <Input
                              name="last_name"
                              label="Last Name"
                              placeholder="Last Name"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.last_name}
                              error={!!showError('last_name')}
                              helperText={showError('last_name')}
                            />
                          </FormGroup>
                          <FormGroup>
                            <Input
                              name="email"
                              label="Email"
                              placeholder="name@email.com"
                              type="email"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.email}
                              error={!!showError('email')}
                              helperText={showError('email')}
                            />
                          </FormGroup>
                        </FormRow>
                      </Section>
                    )}

                    {/* Permissions Section */}
                    {values.capabilities.length > 0 &&
                      values.account_ids.length > 0 && (
                        <Section>
                          <SectionTitle>User Permissions</SectionTitle>

                          {/* Account Tabs */}
                          <TabsContainer>
                            {values.capabilities.map((cap, index) => {
                              const account = values.account_ids.find(
                                (a) => a.value === cap.account_id
                              );
                              return (
                                <Tab
                                  key={index}
                                  type="button"
                                  active={activeTab === index}
                                  onClick={() => setActiveTab(index)}
                                >
                                  {account?.label || 'Unknown'}
                                </Tab>
                              );
                            })}
                          </TabsContainer>

                          {/* Active capability permissions */}
                          {values.capabilities[activeTab] && (
                            <Capabilities
                              values={values.capabilities[activeTab]}
                              handleChange={(e) => {
                                const { name, value } = e.target;
                                handleCapabilityChange(
                                  activeTab,
                                  name,
                                  value,
                                  values,
                                  setFieldValue
                                );
                              }}
                              handleMultipleChange={(changes) => {
                                handleMultipleCapabilityChange(
                                  activeTab,
                                  changes,
                                  values,
                                  setFieldValue
                                );
                              }}
                              handleBlur={handleBlur}
                              errors={{}}
                              touched={{}}
                              mainValue={values}
                              setFieldValue={setFieldValue}
                              capabilityKeys={capabilityKeys}
                              isSelfEdit={isSelfEdit}
                            />
                          )}
                        </Section>
                      )}
                  </ContentWrapper>
                </Card>
              </form>
            );
          }}
        </Formik>
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackEmployeeForm;
